import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  AddCircle as AddCircleIcon,
  Cancel as CancelIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import { Controller } from "react-hook-form";
import axiosInstance from "utilities/axios";
import { useAccess } from "hooks/useAccess";
import { useAssignForm } from "hooks/useAssignForm";
import { useEditAssignmentForm } from "hooks/useEditAssignmentForm";
import { useEndAssignmentForm } from "hooks/useEndAssignmentForm";
import type { AssignFormValues } from "hooks/useAssignForm";
import type { EditAssignmentFormValues } from "hooks/useEditAssignmentForm";
import type { EndAssignmentFormValues } from "hooks/useEndAssignmentForm";

type AppType = "universal" | "cgs";

type AssignmentRow = {
  _id: string;
  organization: { _id: string; name: string };
  application_type: AppType;
  assigned_at: string;
  ended_at: string | null;
  is_active: boolean;
  notes?: string;
};

type MonitoringOrg = { _id: string; name: string };

const APP_TYPE_LABEL: Record<AppType, string> = {
  universal: "Universal",
  cgs: "CGS",
};

function toDateInput(dateStr: string | null | undefined) {
  if (!dateStr) return "";
  return new Date(dateStr).toISOString().slice(0, 10);
}

function fmt(dateStr: string | null) {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("ka-GE");
}

export default function AssignmentsPage() {
  const { canManageOrgs } = useAccess();

  // List state
  const [assignments, setAssignments] = useState<AssignmentRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [filterType, setFilterType] = useState<string>("");
  const [filterActive, setFilterActive] = useState<string>("");

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [orgs, setOrgs] = useState<MonitoringOrg[]>([]);
  const [selectedOrgId, setSelectedOrgId] = useState<string>("");
  const [creating, setCreating] = useState(false);
  const createForm = useAssignForm();

  // Edit dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<AssignmentRow | null>(null);
  const [editing, setEditing] = useState(false);
  const editForm = useEditAssignmentForm();

  // End dialog
  const [endOpen, setEndOpen] = useState(false);
  const [endTarget, setEndTarget] = useState<AssignmentRow | null>(null);
  const [ending, setEnding] = useState(false);
  const endForm = useEndAssignmentForm();

  // Create confirmation dialog
  const [createConfirmOpen, setCreateConfirmOpen] = useState(false);
  const [pendingCreateValues, setPendingCreateValues] = useState<AssignFormValues | null>(null);

  const fetchAssignments = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = {};
      if (filterType) params.application_type = filterType;
      if (filterActive !== "") params.is_active = filterActive;
      const { data } = await axiosInstance.get("/assignments", { params });
      setAssignments(data);
    } catch {
    } finally {
      setLoading(false);
    }
  }, [filterType, filterActive]);

  useEffect(() => { fetchAssignments(); }, [fetchAssignments]);

  // Load orgs when create dialog opens (both agency and monitoring_company)
  useEffect(() => {
    if (!createOpen) return;
    Promise.all([
      axiosInstance.get("/organizations", { params: { org_type: "agency" } }),
      axiosInstance.get("/organizations", { params: { org_type: "monitoring_company" } }),
    ])
      .then(([agencyRes, monitoringRes]) => setOrgs([...agencyRes.data, ...monitoringRes.data]))
      .catch(() => {});
  }, [createOpen]);

  function openCreate() {
    setSelectedOrgId("");
    createForm.reset({ application_type: "universal", assigned_at: "", ended_at: "", is_active: true, notes: "" });
    setCreateOpen(true);
  }

  function handleCreate(values: AssignFormValues) {
    if (!selectedOrgId) return;
    setPendingCreateValues(values);
    setCreateConfirmOpen(true);
  }

  async function confirmCreate() {
    if (!pendingCreateValues || !selectedOrgId) return;
    setCreating(true);
    try {
      const payload: Record<string, string | boolean> = {
        application_type: pendingCreateValues.application_type,
        is_active: pendingCreateValues.is_active,
      };
      if (pendingCreateValues.notes) payload.notes = pendingCreateValues.notes;
      if (pendingCreateValues.assigned_at) payload.assigned_at = pendingCreateValues.assigned_at;
      if (pendingCreateValues.ended_at) payload.ended_at = pendingCreateValues.ended_at;

      await axiosInstance.post(`/organizations/${selectedOrgId}/assign`, payload);
      setCreateConfirmOpen(false);
      setCreateOpen(false);
      setPendingCreateValues(null);
      fetchAssignments();
    } catch (err: any) {
      alert(err?.response?.data?.message || "შეცდომა");
    } finally {
      setCreating(false);
    }
  }

  function openEdit(row: AssignmentRow) {
    setEditTarget(row);
    editForm.reset({
      assigned_at: toDateInput(row.assigned_at),
      ended_at: toDateInput(row.ended_at),
      is_active: row.is_active,
      notes: row.notes || "",
    });
    setEditOpen(true);
  }

  async function handleEdit(values: EditAssignmentFormValues) {
    if (!editTarget) return;
    setEditing(true);
    try {
      await axiosInstance.put(`/assignments/${editTarget._id}`, {
        assigned_at: values.assigned_at || null,
        ended_at: values.ended_at || null,
        is_active: values.is_active,
        notes: values.notes,
      });
      setEditOpen(false);
      fetchAssignments();
    } catch (err: any) {
      alert(err?.response?.data?.message || "შეცდომა");
    } finally {
      setEditing(false);
    }
  }

  function openEnd(row: AssignmentRow) {
    setEndTarget(row);
    endForm.reset({ notes: "" });
    setEndOpen(true);
  }

  async function handleEnd(values: EndAssignmentFormValues) {
    if (!endTarget) return;
    setEnding(true);
    try {
      await axiosInstance.put(`/assignments/${endTarget._id}/end`, { notes: values.notes });
      setEndOpen(false);
      fetchAssignments();
    } catch (err: any) {
      alert(err?.response?.data?.message || "შეცდომა");
    } finally {
      setEnding(false);
    }
  }

  return (
    <Box sx={{ p: 3 }}>
      <Card>
        <CardHeader
          title="მონიტორინგები"
          action={
            canManageOrgs ? (
              <Button variant="contained" startIcon={<AddCircleIcon />} onClick={openCreate}>
                ახალი მონიტორინგის დაწყება
              </Button>
            ) : null
          }
        />
        <CardContent>
          {/* Filters */}
          <Box sx={{ mb: 2, display: "flex", gap: 2 }}>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>ტიპი</InputLabel>
              <Select value={filterType} label="ტიპი" onChange={(e) => setFilterType(e.target.value)}>
                <MenuItem value="">ყველა</MenuItem>
                <MenuItem value="universal">Universal</MenuItem>
                <MenuItem value="cgs">CGS</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>სტატუსი</InputLabel>
              <Select value={filterActive} label="სტატუსი" onChange={(e) => setFilterActive(e.target.value)}>
                <MenuItem value="">ყველა</MenuItem>
                <MenuItem value="true">აქტიური</MenuItem>
                <MenuItem value="false">დასრულებული</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {loading && <LinearProgress sx={{ mb: 1 }} />}

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ორგანიზაცია</TableCell>
                  <TableCell>ტიპი</TableCell>
                  <TableCell>დაწყება</TableCell>
                  <TableCell>დასრულება</TableCell>
                  <TableCell>სტატუსი</TableCell>
                  <TableCell>შენიშვნა</TableCell>
                  {canManageOrgs && <TableCell />}
                </TableRow>
              </TableHead>
              <TableBody>
                {assignments.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                        არ მოიძებნა
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {assignments.map((row) => (
                  <TableRow key={row._id}>
                    <TableCell>{row.organization?.name}</TableCell>
                    <TableCell>
                      <Chip
                        label={APP_TYPE_LABEL[row.application_type] ?? row.application_type}
                        size="small"
                        color={row.application_type === "universal" ? "primary" : "secondary"}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{fmt(row.assigned_at)}</TableCell>
                    <TableCell>{fmt(row.ended_at)}</TableCell>
                    <TableCell>
                      <Chip
                        label={row.is_active ? "აქტიური" : "დასრულებული"}
                        size="small"
                        color={row.is_active ? "success" : "default"}
                        variant={row.is_active ? "filled" : "outlined"}
                      />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 200 }}>
                      <Typography variant="body2" noWrap title={row.notes}>
                        {row.notes || ""}
                      </Typography>
                    </TableCell>
                    {canManageOrgs && (
                      <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                        <Tooltip title="რედაქტირება">
                          <IconButton size="small" onClick={() => openEdit(row)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {row.is_active && (
                          <Tooltip title="მონიტორინგის პერიოდის დასრულება">
                            <IconButton size="small" color="error" onClick={() => openEnd(row)}>
                              <CancelIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Create Assignment Dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <form onSubmit={createForm.handleSubmit(handleCreate)}>
          <DialogTitle>ახალი მონიტორინგის დაწყება</DialogTitle>
          <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "8px !important" }}>
            <FormControl fullWidth size="small">
              <InputLabel>ორგანიზაცია</InputLabel>
              <Select
                value={selectedOrgId}
                label="ორგანიზაცია"
                onChange={(e) => setSelectedOrgId(e.target.value)}
                required
              >
                <MenuItem value=""><em>— აირჩიეთ —</em></MenuItem>
                {orgs.map((o) => (
                  <MenuItem key={o._id} value={o._id}>{o.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Controller
              name="application_type"
              control={createForm.control}
              render={({ field }) => (
                <FormControl fullWidth>
                  <InputLabel>განაცხადის ტიპი</InputLabel>
                  <Select {...field} label="განაცხადის ტიპი">
                    <MenuItem value="universal">Universal</MenuItem>
                    <MenuItem value="cgs">CGS</MenuItem>
                  </Select>
                </FormControl>
              )}
            />
            <Controller
              name="assigned_at"
              control={createForm.control}
              render={({ field }) => (
                <TextField {...field} label="დაწყების თარიღი" type="date" fullWidth InputLabelProps={{ shrink: true }}
                   />
              )}
            />
            <Controller
              name="ended_at"
              control={createForm.control}
              render={({ field }) => (
                <TextField {...field} label="დასრულების თარიღი" type="date" fullWidth InputLabelProps={{ shrink: true }}
                   />
              )}
            />
            <Controller
              name="is_active"
              control={createForm.control}
              render={({ field }) => (
                <Box display="flex" alignItems="center" gap={1} sx={{ cursor: "pointer", userSelect: "none" }}
                  onClick={() => field.onChange(!field.value)}>
                  {field.value ? <CheckBoxIcon color="success" fontSize="small" /> : <CheckBoxOutlineBlankIcon fontSize="small" />}
                  <Typography variant="body2" color={field.value ? "success.main" : "text.secondary"}>მონიტორინგის სესიის გააქტიურება</Typography>
                </Box>
              )}
            />
            <Controller
              name="notes"
              control={createForm.control}
              render={({ field }) => <TextField {...field} label="შენიშვნა" fullWidth multiline rows={2} />}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateOpen(false)}>გაუქმება</Button>
            <Button type="submit" variant="contained" disabled={creating || !selectedOrgId}>დაწყება</Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Edit Assignment Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <form onSubmit={editForm.handleSubmit(handleEdit)}>
          <DialogTitle>
            {editTarget?.organization?.name} — {APP_TYPE_LABEL[editTarget?.application_type as AppType] ?? ""} რედაქტირება
          </DialogTitle>
          <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "8px !important" }}>
            <Controller
              name="assigned_at"
              control={editForm.control}
              render={({ field }) => (
                <TextField {...field} label="დაწყების თარიღი" type="date" fullWidth InputLabelProps={{ shrink: true }} />
              )}
            />
            <Controller
              name="ended_at"
              control={editForm.control}
              render={({ field }) => (
                <TextField {...field} label="დასრულების თარიღი" type="date" fullWidth InputLabelProps={{ shrink: true }} />
              )}
            />
            <Controller
              name="is_active"
              control={editForm.control}
              render={({ field }) => (
                <Box display="flex" alignItems="center" gap={1} sx={{ cursor: "pointer", userSelect: "none" }}
                  onClick={() => field.onChange(!field.value)}>
                  {field.value ? <CheckBoxIcon color="success" fontSize="small" /> : <CheckBoxOutlineBlankIcon fontSize="small" />}
                  <Typography variant="body2" color={field.value ? "success.main" : "text.secondary"}></Typography>
                </Box>
              )}
            />
            <Controller
              name="notes"
              control={editForm.control}
              render={({ field }) => <TextField {...field} label="შენიშვნა" fullWidth multiline rows={2} />}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditOpen(false)}>გაუქმება</Button>
            <Button type="submit" variant="contained" disabled={editing}>შენახვა</Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* End Assignment Dialog */}
      <Dialog open={endOpen} onClose={() => setEndOpen(false)} maxWidth="sm" fullWidth>
        <form onSubmit={endForm.handleSubmit(handleEnd)}>
          <DialogTitle>
            {endTarget?.organization?.name} - {APP_TYPE_LABEL[endTarget?.application_type as AppType] ?? ""} განაცხადების მონიტორინგის დასრულება
          </DialogTitle>
          <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "8px !important" }}>
            <Typography variant="body2" color="error">
              დარწმუნებული ხართ, რომ გსურთ მიმდინარე მონიტორინგის პერიოდის დასრულება?
            </Typography>
            <Controller
              name="notes"
              control={endForm.control}
              render={({ field }) => (
                <TextField {...field} label="მიზეზი / შენიშვნა" fullWidth multiline rows={2} />
              )}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEndOpen(false)}>გაუქმება</Button>
            <Button type="submit" variant="contained" color="error" disabled={ending}>დასრულება</Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Create assignment confirmation dialog */}
      <Dialog open={createConfirmOpen} onClose={() => { setCreateConfirmOpen(false); setPendingCreateValues(null); }} maxWidth="xs" fullWidth>
        <DialogTitle>დადასტურება</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            გსურთ ახალი მონიტორინგის სესიის დაწყება?
          </Typography>
          <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
            ამ ტიპის ორგანიზაციის ყველა სხვა აქტიური {pendingCreateValues?.application_type?.toUpperCase()} მონიტორინგი ავტომატურად დასრულდება.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setCreateConfirmOpen(false); setPendingCreateValues(null); }}>გაუქმება</Button>
          <Button variant="contained" color="primary" disabled={creating} onClick={confirmCreate}>
            დადასტურება
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
